import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { WithId } from '@superfunnel/types/dynamic-page-version';
import { CaptchaProvider } from '@superfunnel/types/form-base';
import {
  ConnectedAccount,
  HeaderParamMapping,
  Integration,
  ObjectParamMapping,
  WebhookIntegration,
  WebhookMethod,
} from '@superfunnel/types/integration';
import { WebhookFilters } from '@superfunnel/types/webhook-filter';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { getHubSpotUrl, getShopifyUrl, getSlackUrl } from '../utils/auth';

export const integrationsColRef = (accountId: string) =>
  collection(db, 'accounts', accountId, 'integrations').withConverter(
    emptyConverter<Integration>(),
  );

export const getIntegrations = async (
  accountId: string,
  dynamicPageId?: string,
) => {
  const fn = httpsCallable<
    { accountId: string; dynamicPageId?: string },
    WithId<Integration>[]
  >(functions, 'integrations/listIntegrations');

  const res = await fn({
    accountId,
    ...(dynamicPageId ? { dynamicPageId } : {}),
  });

  return res.data;
};

export const createWebhookIntegration = async (
  accountId: string,
  type: 'new_lead' | 'new_event' | 'new_partial_lead',
  name: string,
  url: string,
  objectMapping?: Record<string, any>[],
  extraHeaders?: Record<string, any>[],
  dynamicPageIds?: string[],
  formIds?: string[],
  authentication?: {
    type: 'apiKey';
    headerName: string;
    value: string;
  },
  eventTypes?: string[],
  transport?: {
    method?: WebhookMethod;
    contentType?: WebhookIntegration['contentType'];
    filters?: WebhookFilters;
  },
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      type: 'new_lead' | 'new_event' | 'new_partial_lead';
      name: string;
      url: string;
      objectMapping?: Record<string, any>[];
      extraHeaders?: Record<string, any>[];
      dynamicPageIds?: string[];
      formIds?: string[];
      authentication?: {
        type: 'apiKey';
        headerName: string;
        value: string;
      };
      eventTypes?: string[];
      method?: WebhookMethod;
      contentType?: WebhookIntegration['contentType'];
      filters?: WebhookFilters;
    },
    WithId<Integration>
  >(functions, 'integrations/webhooks/createWebhook');

  const res = await fn({
    accountId,
    type,
    name,
    url,
    ...(objectMapping ? { objectMapping } : {}),
    ...(extraHeaders ? { extraHeaders } : {}),
    ...(dynamicPageIds ? { dynamicPageIds } : {}),
    ...(formIds ? { formIds } : {}),
    ...(authentication ? { authentication } : {}),
    ...(eventTypes ? { eventTypes } : {}),
    ...(transport?.method ? { method: transport.method } : {}),
    ...(transport?.contentType ? { contentType: transport.contentType } : {}),
    ...(transport?.filters?.rules.length ? { filters: transport.filters } : {}),
  });

  return res.data;
};

export const updateWebhookIntegration = async (
  accountId: string,
  integrationId: string,
  update: {
    type?: 'new_lead' | 'new_event' | 'new_partial_lead';
    name?: string;
    url?: string;
    objectMapping?: ObjectParamMapping[];
    extraHeaders?: HeaderParamMapping[];
    dynamicPageIds?: string[];
    formIds?: string[];
    authentication?: {
      type: 'apiKey';
      headerName: string;
      value: string;
    } | null;
    status?: 'active' | 'disabled';
    method?: WebhookMethod;
    contentType?: WebhookIntegration['contentType'];
    filters?: WebhookFilters;
    eventTypes?: string[];
  },
) => {
  const fn = httpsCallable<
    typeof update & { accountId: string; integrationId: string },
    { success: true; integrationId: string }
  >(functions, 'integrations/webhooks/updateWebhook');

  const res = await fn({ accountId, integrationId, ...update });
  return res.data;
};

export const setIntegrationStatus = async (
  accountId: string,
  integrationId: string,
  status: 'active' | 'disabled' | 'deleted',
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      integrationId: string;
      status: 'active' | 'disabled' | 'deleted';
    },
    {
      success: true;
      integrationId: string;
      status: 'active' | 'disabled' | 'deleted';
    }
  >(functions, 'integrations/setIntegrationStatus');

  const res = await fn({ accountId, integrationId, status });
  return res.data;
};

export interface IntegrationFormField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  protected: boolean;
  options?: { value: string; label: string }[];
  variantFormIds: string[];
}

export interface IntegrationFormContext {
  id: string;
  kind: 'form' | 'quiz';
  name: string;
  fields: IntegrationFormField[];
  variants: {
    formId: string;
    name: string;
    published: boolean;
    fieldIds: string[];
  }[];
}

export interface IntegrationFormFieldsResult {
  forms: IntegrationFormContext[];
  fields: IntegrationFormField[];
  missingFormIds: string[];
}

export const getIntegrationFormFields = async (
  accountId: string,
  formIds: string[],
) => {
  const fn = httpsCallable<
    { accountId: string; formIds: string[] },
    IntegrationFormFieldsResult
  >(functions, 'integrations/getFormFields');

  const res = await fn({ accountId, formIds });
  return res.data;
};

export const listPageForms = async (
  accountId: string,
  dynamicPageId: string,
  websiteId?: string,
) => {
  const fn = httpsCallable<
    { accountId: string; dynamicPageId: string; websiteId?: string },
    IntegrationFormFieldsResult & {
      dynamicPageId: string;
      websiteIds: string[];
    }
  >(functions, 'integrations/listPageForms');

  const res = await fn({ accountId, dynamicPageId, websiteId });
  return res.data;
};

export interface IntegrationTestResult {
  success: boolean;
  integrationId: string;
  provider: Integration['provider'];
  mode: 'sample' | 'replay';
  testId: string;
  sourceId?: string;
  durationMs: number;
  httpStatus?: number;
  skipped?: true;
  skippedReason?: string;
  error?: string;
}

export const testIntegration = async (
  accountId: string,
  integrationId: string,
  mode: 'sample' | 'replay',
) => {
  const fn = httpsCallable<
    { accountId: string; integrationId: string; mode: 'sample' | 'replay' },
    IntegrationTestResult
  >(functions, 'integrations/testIntegration');

  const res = await fn({ accountId, integrationId, mode });
  return res.data;
};

export const duplicateWebhookIntegration = async (
  accountId: string,
  integrationId: string,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      integrationId: string;
    },
    WithId<Integration>
  >(functions, 'integrations/webhooks/duplicateWebhook');

  const res = await fn({
    accountId,
    integrationId,
  });

  return res.data;
};

// HubSpot Integration Functions

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  groupName: string;
  description?: string;
  options?: Array<{
    label: string;
    value: string;
    displayOrder: number;
  }>;
}

export interface ConnectedAccountSummary {
  id: string;
  provider: string;
  name: string;
  status: 'active' | 'expired' | 'revoked';
  createdAt: any;
  lastUsedAt?: any;
  // Provider-specific
  hubspotPortalId?: string;
  /** Authentication type - 'oauth' or 'private_app' (defaults to 'oauth' for existing records) */
  authType?: 'oauth' | 'private_app';
  captchaProvider?: CaptchaProvider;
  siteKey?: string;
  teamId?: string;
  teamName?: string;
  shopDomain?: string;
  shopName?: string;
  currencyCode?: string;
  webPixelId?: string;
}

/**
 * Get connected accounts collection reference
 */
export const connectedAccountsColRef = (accountId: string) =>
  collection(db, 'accounts', accountId, 'connectedAccounts').withConverter(
    emptyConverter<ConnectedAccount>(),
  );

/**
 * List connected accounts for an account (reads directly from Firestore)
 * Tokens are stored in a subcollection, so this is safe for client-side access
 */
export const listConnectedAccounts = async (
  accountId: string,
  provider?: string,
): Promise<ConnectedAccountSummary[]> => {
  const colRef = connectedAccountsColRef(accountId);

  const q = provider
    ? query(colRef, where('provider', '==', provider))
    : query(colRef);

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      provider: data.provider,
      name: data.name,
      status: data.status,
      createdAt: data.createdAt,
      lastUsedAt: data.lastUsedAt,
      // Provider-specific fields
      ...(data.provider === 'hubspot' && {
        hubspotPortalId: (data as any).hubspotPortalId,
        authType: (data as any).authType || 'oauth', // Default to 'oauth' for existing records
      }),
      ...(data.provider === 'captcha' && {
        captchaProvider: (data as any).captchaProvider,
        siteKey: (data as any).siteKey,
      }),
      ...(data.provider === 'slack' && {
        teamId: (data as any).teamId,
        teamName: (data as any).teamName,
      }),
      ...(data.provider === 'shopify' && {
        shopDomain: (data as any).shopDomain,
        shopName: (data as any).shopName,
        currencyCode: (data as any).currencyCode,
        webPixelId: (data as any).webPixelId,
      }),
    };
  });
};

export const connectCaptchaCredential = async (
  accountId: string,
  name: string,
  siteKey: string,
  secretKey: string,
): Promise<{ success: boolean; connectedAccountId: string }> => {
  const fn = httpsCallable<
    {
      accountId: string;
      name: string;
      siteKey: string;
      secretKey: string;
    },
    { success: boolean; connectedAccountId: string }
  >(functions, 'accounts/captchaCredentials/connect');

  const res = await fn({
    accountId,
    name,
    siteKey,
    secretKey,
  });

  return res.data;
};

export const disconnectAccount = async (
  accountId: string,
  connectedAccountId: string,
  disableIntegrations = true,
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      disableIntegrations?: boolean;
    },
    { success: boolean; message: string; disabledIntegrations: number }
  >(functions, 'integrations/connectedAccounts/disconnect');

  const res = await fn({ accountId, connectedAccountId, disableIntegrations });
  return res.data;
};

/**
 * Initiate HubSpot OAuth flow (client-side)
 * Creates an OAuth request document and returns the authorization URL
 */
export const initHubSpotOAuth = async (
  accountId: string,
  userId: string,
  from?: string,
) => {
  // Create OAuth request document for state management
  const oauthRequestRef = doc(
    collection(db, 'accounts', accountId, 'oauthRequests'),
  );

  await setDoc(oauthRequestRef, {
    requestId: oauthRequestRef.id,
    accountId,
    userId,
    provider: 'hubspot',
    from: from || '/integrations',
    createdAt: new Date(),
  });

  // Generate authorization URL with state
  const authUrl = getHubSpotUrl(oauthRequestRef.id);

  return {
    authUrl,
    requestId: oauthRequestRef.id,
  };
};

/**
 * Initiate Slack OAuth flow (client-side)
 * Creates an OAuth request document and returns the authorization URL
 */
export const initSlackOAuth = async (
  accountId: string,
  userId: string,
  from?: string,
) => {
  // Create OAuth request document for state management
  const oauthRequestRef = doc(
    collection(db, 'accounts', accountId, 'oauthRequests'),
  );

  await setDoc(oauthRequestRef, {
    requestId: oauthRequestRef.id,
    accountId,
    userId,
    provider: 'slack',
    from: from || '/settings',
    createdAt: new Date(),
  });

  // Generate authorization URL with state
  const authUrl = getSlackUrl(oauthRequestRef.id);

  return {
    authUrl,
    requestId: oauthRequestRef.id,
  };
};

/**
 * Start the Shopify OAuth flow for a specific store.
 *
 * Shopify authorises on the merchant's own domain, so the shop must be supplied
 * up front rather than chosen on the provider's side.
 */
export const initShopifyOAuth = async (
  accountId: string,
  userId: string,
  shopDomain: string,
  from?: string,
) => {
  const oauthRequestRef = doc(
    collection(db, 'accounts', accountId, 'oauthRequests'),
  );

  await setDoc(oauthRequestRef, {
    requestId: oauthRequestRef.id,
    accountId,
    userId,
    provider: 'shopify',
    shopDomain,
    from: from || '/integrations',
    createdAt: new Date(),
  });

  return {
    authUrl: getShopifyUrl(oauthRequestRef.id, shopDomain),
    requestId: oauthRequestRef.id,
  };
};

/**
 * Attach a store that installed itself from Shopify.
 *
 * The install cannot know which account it belongs to -- Shopify requires the
 * permission grant before anything else -- so the token waits under a claim
 * until somebody signs in and takes it.
 */
export const claimShopifyInstall = async (input: {
  accountId: string;
  claimId: string;
}) => {
  const fn = httpsCallable<
    typeof input,
    { connectedAccountId: string; shopDomain: string }
  >(functions, 'integrations/shopify/claimInstall');

  const res = await fn(input);
  return res.data;
};

/** The store's collections, for choosing what the sync should pull. */
export const listShopifyCollections = async (
  accountId: string,
  connectedAccountId: string,
) => {
  const fn = httpsCallable<
    { accountId: string; connectedAccountId: string },
    { collections: Array<{ id: string; handle: string; title: string }> }
  >(functions, 'integrations/shopify/listCollections');

  const res = await fn({ accountId, connectedAccountId });
  return res.data.collections;
};

/** Create the synced products collection and kick off the first sync. */
export const createShopifyProductCollection = async (input: {
  accountId: string;
  connectedAccountId: string;
  name?: string;
  shopifyCollectionIds: string[];
}) => {
  const fn = httpsCallable<
    typeof input,
    { collectionId: string; jobId: string }
  >(functions, 'integrations/shopify/createCollection');

  const res = await fn(input);
  return res.data;
};

/** Install or remove the purchase-tracking pixel on a store. Support admins only. */
export const setShopifyPurchaseTracking = async (input: {
  accountId: string;
  connectedAccountId: string;
  enabled: boolean;
}) => {
  const fn = httpsCallable<typeof input, { enabled: boolean }>(
    functions,
    'integrations/shopify/setPurchaseTracking',
  );

  const res = await fn(input);
  return res.data;
};

/** Where the merchant approves the app's current scopes. Support admins only. */
export const getShopifyApprovalLink = async (input: {
  accountId: string;
  connectedAccountId: string;
}) => {
  const fn = httpsCallable<typeof input, { url: string }>(
    functions,
    'integrations/shopify/approvalLink',
  );

  const res = await fn(input);
  return res.data;
};

/** Resync a synced collection, optionally changing what it pulls. */
export const resyncShopifyProductCollection = async (input: {
  accountId: string;
  collectionId: string;
  shopifyCollectionIds?: string[];
}) => {
  const fn = httpsCallable<typeof input, { jobId: string }>(
    functions,
    'integrations/shopify/resyncCollection',
  );

  const res = await fn(input);
  return res.data;
};

export const getHubSpotProperties = async (
  accountId: string,
  connectedAccountId: string,
) => {
  const fn = httpsCallable<
    { accountId: string; connectedAccountId: string },
    {
      properties: HubSpotProperty[];
      totalCount: number;
      filteredCount: number;
    }
  >(functions, 'integrations/hubspot/getProperties');

  const res = await fn({ accountId, connectedAccountId });
  return res.data;
};

export const createHubSpotIntegration = async (
  accountId: string,
  connectedAccountId: string,
  data: {
    name?: string;
    type?: 'new_lead' | 'new_event' | 'new_partial_lead';
    status?: 'active' | 'disabled' | 'draft';
    dynamicPageIds?: string[];
    formIds?: string[];
    fieldMappings?: Record<string, any>[];
    eventTypes?: string[];
  },
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      name?: string;
      type?: 'new_lead' | 'new_event' | 'new_partial_lead';
      status?: 'active' | 'disabled' | 'draft';
      dynamicPageIds?: string[];
      formIds?: string[];
      fieldMappings?: Record<string, any>[];
      eventTypes?: string[];
    },
    any
  >(functions, 'integrations/hubspot/updateIntegration');

  const res = await fn({ accountId, connectedAccountId, ...data });
  return res.data;
};

export const updateHubSpotIntegration = async (
  accountId: string,
  integrationId: string,
  connectedAccountId: string,
  update: {
    name?: string;
    type?: 'new_lead' | 'new_event' | 'new_partial_lead';
    status?: 'active' | 'disabled';
    dynamicPageIds?: string[];
    formIds?: string[];
    fieldMappings?: Record<string, any>[];
    eventTypes?: string[];
  },
) => {
  const fn = httpsCallable<
    {
      accountId: string;
      integrationId: string;
      connectedAccountId: string;
      name?: string;
      type?: 'new_lead' | 'new_event' | 'new_partial_lead';
      status?: 'active' | 'disabled';
      dynamicPageIds?: string[];
      formIds?: string[];
      fieldMappings?: Record<string, any>[];
      eventTypes?: string[];
    },
    any
  >(functions, 'integrations/hubspot/updateIntegration');

  const res = await fn({
    accountId,
    integrationId,
    connectedAccountId,
    ...update,
  });
  return res.data;
};

// =============================================================================
// HubSpot Private App Functions
// =============================================================================

/**
 * Connect a HubSpot Private App using an access token
 *
 * Validates the token against HubSpot's API, retrieves the portal ID,
 * and creates a new connected account with the token stored securely.
 *
 * @see https://developers.hubspot.com/docs/api/private-apps
 */
export const connectHubSpotPrivateApp = async (
  accountId: string,
  accessToken: string,
  name: string,
): Promise<{
  success: boolean;
  connectedAccountId: string;
  portalId: string;
}> => {
  const fn = httpsCallable<
    {
      accountId: string;
      accessToken: string;
      name: string;
    },
    {
      success: boolean;
      connectedAccountId: string;
      portalId: string;
    }
  >(functions, 'integrations/hubspot/connectPrivateApp');

  const res = await fn({ accountId, accessToken, name });
  return res.data;
};

/**
 * Update the Private App token for an existing connected account
 *
 * Validates the new token against HubSpot's API to ensure it's valid
 * and belongs to the same portal, then updates the stored token.
 *
 * Use this when the user regenerates their Private App token in HubSpot.
 */
export const updateHubSpotPrivateAppToken = async (
  accountId: string,
  connectedAccountId: string,
  accessToken: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<
    {
      accountId: string;
      connectedAccountId: string;
      accessToken: string;
    },
    { success: boolean }
  >(functions, 'integrations/hubspot/updatePrivateAppToken');

  const res = await fn({ accountId, connectedAccountId, accessToken });
  return res.data;
};
